import React, { createContext, useMemo } from 'react';
import {
  iosCreatorUARegex,
  iosListenerUARegex,
  androidCreatorUARegex,
  androidListenerUARegex,
  desktopUARegex,
  versionNumberRegex,
  isEmbedded,
  isBrowserEmbedded
} from 'lib/useragents';
import { compare } from 'compare-versions';

interface ContextProps {
  isEmbed: boolean;
  isBrowserEmbed: boolean;
  isiOSCreatorApp: boolean;
  isiOSCreatorAppVersion: (minVersion: string) => boolean;
  isAndroidCreatorApp: boolean;
  isAndroidCreatorAppVersion: (minVersion: string) => boolean;
  isiOSListenerApp: boolean;
  isiOSListenerAppVersion: (minVersion: string) => boolean;
  isAndroidListenerApp: boolean;
  isAndroidListenerAppVersion: (minVersion: string) => boolean;
  isNativeAppWebview: boolean;
  isMobileAppWebview: boolean;
  isDACreatorApp: boolean;
  isDACreatorAppVersion: (minVersion: string) => boolean;
  isListenerAppWebview: boolean;
  isCreatorAppWebview: boolean;
  deviceClassName: string;
}

export const UserAgentContext = createContext<ContextProps>({
  isEmbed: false,
  isBrowserEmbed: false,
  isiOSCreatorApp: false,
  isiOSCreatorAppVersion: () => false,
  isAndroidCreatorApp: false,
  isAndroidCreatorAppVersion: () => false,
  isiOSListenerApp: false,
  isiOSListenerAppVersion: () => false,
  isAndroidListenerApp: false,
  isAndroidListenerAppVersion: () => false,
  isNativeAppWebview: false,
  isMobileAppWebview: false,
  isDACreatorApp: false,
  isDACreatorAppVersion: () => false,
  isListenerAppWebview: false,
  isCreatorAppWebview: false,
  deviceClassName: ''
});

const testRegex = (uaregex: RegExp, minVersion: string): boolean => {
  const ua = navigator.userAgent;
  const matcher = new RegExp(uaregex);
  const versionNumber = ua.match(versionNumberRegex);
  if (versionNumber && matcher.test(ua)) {
    return compare(versionNumber[1], minVersion, '>=');
  } else {
    return false;
  }
};

const isiOSListenerAppVersion = (minVersion: string) =>
  testRegex(iosListenerUARegex, minVersion);
const isAndroidListenerAppVersion = (minVersion: string) =>
  testRegex(androidListenerUARegex, minVersion);
const isiOSCreatorAppVersion = (minVersion: string) =>
  testRegex(iosCreatorUARegex, minVersion);
const isAndroidCreatorAppVersion = (minVersion: string) =>
  testRegex(androidCreatorUARegex, minVersion);
const isDACreatorAppVersion = (minVersion: string) =>
  testRegex(desktopUARegex, minVersion);

const buildContextValue = (): ContextProps => {
  const ua = navigator.userAgent;

  const isiOSCreatorApp = iosCreatorUARegex.test(ua);
  const isiOSListenerApp = iosListenerUARegex.test(ua);
  const isAndroidCreatorApp = androidCreatorUARegex.test(ua);
  const isAndroidListenerApp = androidListenerUARegex.test(ua);
  const isDACreatorApp = desktopUARegex.test(ua);

  const isEmbed = isEmbedded();
  const isBrowserEmbed = isBrowserEmbedded();

  const isNativeAppWebview =
    isiOSCreatorApp ||
    isAndroidCreatorApp ||
    isDACreatorApp ||
    isiOSListenerApp ||
    isAndroidListenerApp;
  const isMobileAppWebview =
    isiOSCreatorApp ||
    isAndroidCreatorApp ||
    isiOSListenerApp ||
    isAndroidListenerApp;
  const isListenerAppWebview = isiOSListenerApp || isAndroidListenerApp;
  const isCreatorAppWebview =
    isiOSCreatorApp || isAndroidCreatorApp || isDACreatorApp;

  let deviceClassName = '';
  if (isiOSCreatorApp) deviceClassName = 'ios ios-creator';
  else if (isiOSListenerApp) deviceClassName = 'ios ios-listener';
  else if (isAndroidCreatorApp) deviceClassName = 'android android-creator';
  else if (isAndroidListenerApp) deviceClassName = 'android android-listener';
  else if (isDACreatorApp) deviceClassName = 'creator-da';
  if (isEmbed) deviceClassName = 'embed';
  if (isBrowserEmbed) deviceClassName = 'browser-embed';
  if (isListenerAppWebview) deviceClassName += ' app-listener';
  if (isCreatorAppWebview) deviceClassName += ' app-creator';
  if (isDACreatorAppVersion(import.meta.env.VITE_DA_SINGLE_VIEW_MIN_VERSION)) {
    deviceClassName += ' creator-da creator-da-min-34';
  }

  return {
    isEmbed,
    isBrowserEmbed,
    isiOSCreatorApp,
    isiOSCreatorAppVersion,
    isAndroidCreatorApp,
    isAndroidCreatorAppVersion,
    isiOSListenerApp,
    isiOSListenerAppVersion,
    isAndroidListenerApp,
    isAndroidListenerAppVersion,
    isNativeAppWebview,
    isMobileAppWebview,
    isDACreatorApp,
    isDACreatorAppVersion,
    isListenerAppWebview,
    isCreatorAppWebview,
    deviceClassName
  };
};

const UserAgentProvider = ({
  children
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const contextValue = useMemo(() => buildContextValue(), []);
  return (
    <UserAgentContext.Provider value={contextValue}>
      {children}
    </UserAgentContext.Provider>
  );
};

export default UserAgentProvider;
